import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Plus, Trash2, Edit, Upload } from "lucide-react";
import "./AdminEdit.css";
import * as api from "../api/api";
import LoadingSpinner from "./LoadingSpinner";

const AdminTeamEdit = () => {
  const navigate = useNavigate();
  const { locationSlug } = useParams();
  const [team, setTeam] = useState([]);
  const [pendingTeam, setPendingTeam] = useState({ add: [], remove: [] });
  const [editingMember, setEditingMember] = useState(null);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);

  // -----------------------------
  // FETCH team members
  // -----------------------------
  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const res = await api.getTeam(locationSlug);
        const members = res.map((m) => ({ ...m, id: m.id || m._id }));
        setTeam(members);
        setPendingTeam({ add: [], remove: [] });
      } catch (err) {
        console.error("Erreur fetch team:", err);
        setTeam([]);
        setPendingTeam({ add: [], remove: [] });
      } finally {
        setLoading(false);
      }
    };
    fetchTeam();
  }, [locationSlug]);

  // -----------------------------
  // Ajouter nouveau membre temporaire
  // -----------------------------
  const addMember = () => {
    const newMember = {
      id: "temp-" + Date.now(),
      name: "Nouveau Membre",
      role: "Kinésithérapeute",
      photo: "",
      bio: "Biographie du membre...",
      specialties: [],
      doctolib: undefined,
      location_slug: locationSlug,
      order: team.length + 1,
    };
    setEditingMember(newMember);
  };

  // -----------------------------
  // Supprimer membre
  // -----------------------------
  const removeMember = async (member) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce membre ?"))
      return;

    try {
      if (!member.id || member.id.startsWith("temp-")) {
        // remove locally and from pending adds if present
        setTeam((prev) => prev.filter((m) => m.id !== member.id));
        setPendingTeam((prev) => ({
          ...prev,
          add: prev.add.filter((m) => m.id !== member.id),
        }));
      } else {
        // mark for deletion and remove from local list
        setTeam((prev) => prev.filter((m) => m.id !== member.id));
        setPendingTeam((prev) => ({
          ...prev,
          remove: [...prev.remove, member.id],
        }));
      }
      if (editingMember?.id === member.id) setEditingMember(null);
    } catch (err) {
      console.error("Erreur suppression membre:", err);
    }
  };

  // -----------------------------
  // Modifier champs du membre en cours d'édition
  // -----------------------------
  const updateMember = (field, value) => {
    setEditingMember((prev) => ({ ...prev, [field]: value }));
  };

  const addSpecialty = () =>
    updateMember("specialties", [
      ...(editingMember.specialties || []),
      "Nouvelle spécialité",
    ]);

  const removeSpecialty = (index) =>
    updateMember(
      "specialties",
      editingMember.specialties.filter((_, i) => i !== index),
    );

  const updateSpecialty = (index, value) => {
    const newSpecialties = [...editingMember.specialties];
    newSpecialties[index] = value;
    updateMember("specialties", newSpecialties);
  };

  // -----------------------------
  // Sélection d'une photo depuis le Finder
  // -----------------------------
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const localURL = URL.createObjectURL(file);
    setEditingMember((prev) => ({ ...prev, photo: localURL, _file: file }));
  };

  // -----------------------------
  // Sauvegarde locale d'un membre
  // -----------------------------
  const saveEditingMember = () => {
    if (!editingMember) return;

    setTeam((prevTeam) => {
      const exists = prevTeam.some((m) => m.id === editingMember.id);
      if (exists) {
        return prevTeam.map((m) =>
          m.id === editingMember.id ? editingMember : m,
        );
      }
      // new temporary member: add to visible list and to pending adds
      const next = [...prevTeam, editingMember];
      setPendingTeam((prev) => ({
        ...(prev && typeof prev === "object" ? prev : { add: [], remove: [] }),
        add: [...((prev && prev.add) || []), editingMember],
      }));
      return next;
    });

    setEditingMember(null);
  };

  // -----------------------------
  // Enregistre toutes les modifications dans la DB
  // -----------------------------
  const handleSaveAll = async () => {
    try {
      setSaved(false);

      // 1) Upload files for pending adds
      const adds =
        pendingTeam && Array.isArray(pendingTeam.add) ? pendingTeam.add : [];
      const uploads = await Promise.all(
        adds.map(async (m) => {
          const copy = { ...m };
          if (copy._file) {
            try {
              // Use the generic file upload so team photos are not inserted into the gallery
              const uploaded = await api.uploadFile(copy._file);
              copy.photo = uploaded.url;
            } catch (err) {
              console.error("Failed to upload photo for", copy.name, err);
            }
            delete copy._file;
          }
          return copy;
        }),
      );

      // 2) Create new members on server and replace temp items with returned docs
      const createdDocs = [];
      for (const member of uploads) {
        const payload = { ...member };
        const tempId = payload.id;
        delete payload.id;
        try {
          const created = await api.addTeamMember(payload);
          // created should be the serialized doc with id
          createdDocs.push({ tempId, doc: created });
        } catch (err) {
          console.error(
            "Failed to create member on server for",
            payload.name,
            err,
          );
        }
      }

      // Replace temp entries in local team with server-returned docs (if any)
      if (createdDocs.length > 0) {
        setTeam((prev) => {
          // remove any temp entries that were created and add server docs
          let next = prev.filter((m) => !String(m.id).startsWith("temp-"));
          // include server docs
          for (const c of createdDocs) {
            const d = c.doc;
            next = [...next, { ...d, id: d.id || d._id }];
          }
          return next;
        });
      }
      // clear add queue to avoid duplicates
      setPendingTeam((prev) => ({ ...prev, add: [] }));

      // 3) Delete marked members
      const removes =
        pendingTeam && Array.isArray(pendingTeam.remove)
          ? pendingTeam.remove
          : [];
      for (const id of removes) {
        try {
          await api.deleteTeamMember(id);
        } catch (err) {
          console.error("Failed to delete member", id, err);
        }
      }

      // 4) Update existing members (non-temp)
      // 4) Upload photos for existing members that were edited (have _file), then update them
      for (const member of team) {
        if (!member.id || String(member.id).startsWith("temp-")) continue;

        const mcopy = { ...member };
        if (mcopy._file) {
          try {
            const uploaded = await api.uploadFile(mcopy._file);
            mcopy.photo = uploaded.url;
          } catch (err) {
            console.error(
              "Failed to upload photo for existing member",
              mcopy.name,
              err,
            );
          }
          delete mcopy._file;
        }

        const payload = { ...mcopy };
        delete payload.id;
        try {
          await api.updateTeamMember(member.id, payload);
        } catch (err) {
          console.error("Failed to update member", member.id, err);
        }
      }

      // Reload team from server
      const refreshed = await api.getTeam(locationSlug);
      const members = refreshed.map((m) => ({ ...m, id: m.id || m._id }));
      setTeam(members);
      setPendingTeam({ add: [], remove: [] });

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error("Erreur lors de la sauvegarde", err);
      alert("Erreur lors de l’enregistrement. Voir la console.");
    }
  };

  // -----------------------------
  // Render
  // -----------------------------
  if (loading) return <LoadingSpinner />;

  return (
    <div className="admin-edit-page">
      <div className="admin-header">
        <div className="container">
          <button
            className="back-btn"
            onClick={() =>
              navigate("/admin/dashboard", {
                state: { selectedLocation: locationSlug },
              })
            }
          >
            <ArrowLeft size={20} /> Retour au dashboard
          </button>
          <div className="admin-header-content">
            <h1 className="heading-md">Modifier - Équipe</h1>
            <button
              className="btn-primary"
              onClick={handleSaveAll}
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <Save size={18} /> {saved ? "✓ Sauvegardé" : "Sauvegarder"}
            </button>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <div className="edit-section">
            <div className="section-header">
              <h2 className="heading-sm">Membres de l'Équipe</h2>
              <button className="btn-secondary btn-small" onClick={addMember}>
                <Plus size={16} /> Ajouter un membre
              </button>
            </div>

            <div className="team-list">
              {team.map((member) => (
                <div key={member.id} className="team-member-item">
                  <img
                    src={member.photo || "/placeholder.png"}
                    alt={member.name}
                    className="member-thumbnail"
                  />
                  <div className="member-details">
                    <h3 className="heading-sm">{member.name}</h3>
                    <p
                      className="body-md"
                      style={{ color: "var(--text-secondary)" }}
                    >
                      {member.role}
                    </p>
                  </div>
                  <div className="member-actions">
                    <button
                      className="btn-secondary btn-small"
                      onClick={() => setEditingMember(member)}
                    >
                      <Edit size={16} /> Modifier
                    </button>
                    <button
                      className="btn-icon-delete"
                      onClick={() => removeMember(member)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {editingMember && (
            <div
              className="modal-overlay"
              onClick={() => setEditingMember(null)}
            >
              <div
                className="modal-content modal-large"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h2 className="heading-md">Modifier le Membre</h2>
                  <button
                    className="modal-close"
                    onClick={() => setEditingMember(null)}
                  >
                    ×
                  </button>
                </div>

                <div className="modal-body">
                  {/* Sélection de la photo depuis le Finder */}
                  <div className="form-group">
                    <label className="form-label">Photo</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleFileSelect}
                    />
                    <button
                      className="btn-secondary"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload size={16} /> Choisir un fichier
                    </button>

                    {editingMember.photo && (
                      <div className="photo-preview">
                        <img src={editingMember.photo} alt="Aperçu" />
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Nom</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editingMember.name}
                      onChange={(e) => updateMember("name", e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Rôle</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editingMember.role}
                      onChange={(e) => updateMember("role", e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Biographie</label>
                    <textarea
                      className="form-textarea"
                      rows={4}
                      value={editingMember.bio}
                      onChange={(e) => updateMember("bio", e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Lien Doctolib</label>
                    <input
                      type="url"
                      className="form-input"
                      value={editingMember.doctolib || ""}
                      onChange={(e) => updateMember("doctolib", e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <div className="section-header">
                      <label className="form-label">Spécialités</label>
                      <button
                        className="btn-secondary btn-small"
                        onClick={addSpecialty}
                      >
                        <Plus size={14} /> Ajouter
                      </button>
                    </div>
                    {editingMember.specialties.map((specialty, index) => (
                      <div key={index} className="list-item-simple">
                        <input
                          type="text"
                          className="form-input"
                          value={specialty}
                          onChange={(e) =>
                            updateSpecialty(index, e.target.value)
                          }
                        />
                        <button
                          className="btn-icon-delete"
                          onClick={() => removeSpecialty(index)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="modal-actions">
                    <button
                      className="btn-secondary"
                      onClick={() => setEditingMember(null)}
                    >
                      Annuler
                    </button>
                    <button className="btn-primary" onClick={saveEditingMember}>
                      <Save size={18} /> Enregistrer les modifications
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminTeamEdit;
